// @flow
import React, { PureComponent } from 'react';
import snakeCase from 'lodash/fp/snakeCase';
import moment from 'moment';
import type { PaymentMethod } from '../../stores/forms/checkout';

import '../../../static/scss/components/paymentMethodItem.scss';

type Props = {|
  paymentMethod: PaymentMethod,
  onDelete?: number => void,
  onSelect?: number => void,
  isLocked?: boolean,
  isSelectable?: boolean,
  setModel?: number => void
|};

const GoogleTemplate = () => {
  return (
    <div
      data-cy="payment-method-details"
      className="c-paymentMethod__details c-paymentMethod__details--googlePay"
    >
      <p>
        <span
          data-cy="payment-method-details-standout-text"
          className="c-paymentMethod__text--standout"
        >
          GOOGLE PAY
        </span>
      </p>
    </div>
  );
};

const AppleTemplate = () => {
  return (
    <div
      data-cy="payment-method-details"
      className="c-paymentMethod__details c-paymentMethod__details--applePay"
    >
      <p>
        <span
          data-cy="payment-method-details-standout-text"
          className="c-paymentMethod__text--standout"
        >
          APPLE PAY
        </span>
      </p>
    </div>
  );
};

class PaymentMethodItem extends PureComponent<Props> {
  constructor(props: Props) {
    super(props);
    (this: any).onDelete = this.onDelete.bind(this);
    (this: any).onSelect = this.onSelect.bind(this);
  }

  onDelete() {
    const { paymentMethod, onDelete } = this.props;

    if (paymentMethod.id && onDelete) {
      onDelete(paymentMethod.id);
    } else {
      throw new Error('Trying to delete a paymentMethod with no ID');
    }
  }

  onSelect() {
    const { paymentMethod, onSelect, isSelectable, setModel } = this.props;
    const { id } = paymentMethod;
    if (id && onSelect && isSelectable) {
      onSelect(id);
      setModel({
        paymentMethodId: id
      });
    }
  }

  corporateTemplate() {
    const { name } = this.props.paymentMethod;

    return (
      <div
        data-cy="payment-method-details"
        className="c-paymentMethod__details"
      >
        <p>
          <span
            data-cy="payment-method-details-standout-text"
            className="c-paymentMethod__text--standout"
          >
            Corporate
          </span>
        </p>
        <p className="c-paymentMethod__text--muted c-paymentMethod__text--subtitle">
          {name}
        </p>
      </div>
    );
  }

  multiUseageTemplate() {
    const { name } = this.props.paymentMethod;

    return (
      <div
        data-cy="payment-method-details"
        className="c-paymentMethod__details"
      >
        <p>
          <span
            data-cy="payment-method-details-standout-text"
            className="c-paymentMethod__text--standout"
          >
            Multi Use
          </span>
        </p>
        <p className="c-paymentMethod__text--muted c-paymentMethod__text--subtitle">
          {name}
        </p>
      </div>
    );
  }

  cardTemplate() {
    const { cardType, lastFour, expiryDate } = this.props.paymentMethod;
    const { isLocked } = this.props;

    return (
      <>
        <div
          data-cy="payment-method-details"
          className="c-paymentMethod__details"
        >
          <p>
            <span
              data-cy="payment-method-details-standout-text"
              className="c-paymentMethod__text--standout"
            >
              {cardType}
            </span>{' '}
            ending in {lastFour}
          </p>
          <p className="c-paymentMethod__text--muted c-paymentMethod__text--subtitle">
            Expires: {moment(expiryDate).format('MM/YY')}
          </p>
        </div>
        {!isLocked && (
          <button
            className="c-paymentMethod__delete"
            onClick={this.onDelete}
            type="button"
          >
            Delete
          </button>
        )}
      </>
    );
  }

  paypalTemplate() {
    const { isLocked } = this.props;

    return (
      <>
        <div
          data-cy="payment-method-details"
          className="c-paymentMethod__details"
        >
          <p>
            <span
              data-cy="payment-method-details-standout-text"
              className="c-paymentMethod__text--standout"
            >
              Paypal
            </span>{' '}
            secure checkout
          </p>
          <p className="c-paymentMethod__text--muted c-paymentMethod__text--subtitle">
            Account linked
          </p>
        </div>
        {!isLocked && (
          <button
            className="c-paymentMethod__delete"
            onClick={this.onDelete}
            type="button"
          >
            Unlink
          </button>
        )}
      </>
    );
  }

  getTemplate(source: ?string, cardType: ?string) {
    if (cardType === 'Corporate') {
      return this.corporateTemplate();
    }

    switch (source) {
      case 'multi_usage':
        return this.multiUseageTemplate();

      case 'google_pay':
        return <GoogleTemplate />;

      case 'apple_pay':
        return <AppleTemplate />;

      case 'paypal':
        return this.paypalTemplate();

      default:
        return this.cardTemplate();
    }
  }

  render() {
    const { isActive, source, cardType } = this.props.paymentMethod;
    const { isLocked, isSelectable } = this.props;
    const card = cardType || '';

    const activeClass =
      isActive && isSelectable ? 'c-paymentMethod--active' : '';
    const lockedClass = isLocked ? 'c-paymentMethod--locked' : '';
    const nonSelectClass = !isSelectable ? 'c-paymentMethod--no-edit' : '';

    return (
      <div
        className={`c-paymentMethod ${activeClass} ${lockedClass} ${nonSelectClass} c-paymentMethod__${snakeCase(
          card.toLowerCase()
        )}`}
        onClick={this.onSelect}
      >
        {this.getTemplate(source, cardType)}
      </div>
    );
  }
}

PaymentMethodItem.defaultProps = {
  onDelete: () => {},
  onSelect: () => {},
  isLocked: false,
  isSelectable: false,
  setModel: () => {}
};

export default PaymentMethodItem;
